
/**
 * Module dependencies.
 */

import { setStyledProps } from 'react-components/styles';
import { switchProp, theme } from 'styled-tools';
import { typography } from './typography-styles';
import styled, { css } from 'styled-components';

/**
 * Set type style.
 */

function setTypeStyle(element: keyof typeof typography.styles): any {
  return css`
    ${typography.styles[element]}
  `;
}

/**
 * Heading level cases.
 */

const headingLevelCases = {
  h1: css`
    ${setTypeStyle('heading1')}
  `,
  h2: css`
    ${setTypeStyle('heading2')}
  `,
  h3: css`
    ${setTypeStyle('heading3')}
  `,
  h4: css`
    ${setTypeStyle('heading4')}
  `
};

/**
 * `HeadingProps` type.
 */

type HeadingProps = {
  level?: keyof typeof headingLevelCases;
};

/**
 * Export `Heading` component.
 */

export const Heading = styled.p<HeadingProps>`
  ${theme('typography.fontSettings')}

  ${switchProp('level', headingLevelCases, headingLevelCases.h3)}

  ${setStyledProps}
`;

/**
 * Text size cases.
 */

const textSizeCases = {
  extraSmall: css`
    ${setTypeStyle('textExtraSmall')}
  `,
  large: css`
    ${setTypeStyle('textLarge')}
  `,
  medium: css`
    ${setTypeStyle('textMedium')}
  `,
  normal: css`
    ${setTypeStyle('textNormal')}
  `,
  small: css`
    ${setTypeStyle('textSmall')}
  `
};

/**
 * Export `TextSize` type.
 */

export type TextSize = keyof typeof textSizeCases;

/**
 * `TextProps` type.
 */

type TextProps = {
  size?: TextSize;
};

/**
 * Export `Text` component.
 */

export const Text = styled.p<TextProps>`
  ${theme('typography.fontSettings')}

  ${switchProp('size', textSizeCases, textSizeCases.normal)}

  ${setStyledProps}
`;

/**
 * Caption size cases.
 */

const captionSizeCases = {
  extraSmall: css`
    ${setTypeStyle('captionExtraSmall')}
  `,
  medium: css`
    ${setTypeStyle('captionMedium')}
  `,
  small: css`
    ${setTypeStyle('captionSmall')}
  `
};

/**
 * `CaptionProps` type.
 */

type CaptionProps = {
  size: keyof typeof captionSizeCases;
};

/**
 * Export `Caption` component.
 */

export const Caption = styled.label<CaptionProps>`
  ${theme('typography.fontSettings')}

  ${switchProp('size', captionSizeCases)}

  ${setStyledProps}
`;
